import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { showActionNotification } from '../../../../../components/notification'
import { loginState } from '../../../../login/login.slice'
import {
    addStoryMapComment,
    createJourney,
    createRelease,
    createStep,
    createStory,
    deleteJourney,
    deleteRelease,
    deleteStep,
    deleteStory,
    deleteStoryMapComment,
    editStoryMapComment,
    extractPersistedId,
    getStoryMapDetail,
    isStoryMapBoardFilterRequestReady,
    getStoryMapCard,
    isMongoObjectId,
    buildStoryMapCardLink,
    buildStoryMapBoardLink,
    copyStoryMapLinkToClipboard,
    listStoryMapComments,
    listStoryMapActivity,
    mapGetCardToBoardFields,
    updateJourney,
    updateRelease,
    updateStep,
    updateStory,
    updateStoryMap,
    updateStoryMapIndex,
    EMPTY_STORY_MAP_BOARD_FILTERS,
} from '../../../storyMap.service'
import { descriptionsAreEqual } from '../components/StoryMapDescriptionEditor'
import { createUid } from '../../ProjectStoryMap.dummyData'
import styles from '../../ProjectStoryMapBoard.module.css'
import {
    buildBoardTracks,
    buildGridTemplateColumns,
    flattenSteps,
    getJourneyNumber,
    normalizeHexColor,
    normalizeStoryStatus,
    parseCellId,
    isSameStoryCell,
    reorderStoriesInCell,
    toStoryStatusApiValue,
    parseJourneyLabel,
    parseStepLabel,
    parseStepLaneId,
    parseStepLaneEndId,
    renumberJourneySteps,
    buildBoardNavigationContext,
    countBoardFilterMatches,
    getFilterMatchSelector,
    getFilterMatchCardId,
    resolveEditTitleName,
} from '../utils'

const isTempId = (id) => !isMongoObjectId(id)

const notifyDetailUpdateSuccess = (message) => {
    if (!message) return
    showActionNotification({
        type: 'success',
        message,
        notificationKey: 'story-map-detail-updated',
    })
}

const getJourneyUpdateMessage = (apiPayload) => {
    const hasName = apiPayload.name != null
    const hasDescription = apiPayload.description != null

    if (hasName && hasDescription) return 'Journey details saved'
    if (hasName) return 'Journey name updated'
    if (hasDescription) return 'Journey description saved'
    return 'Journey details saved'
}

const getStepUpdateMessage = (apiPayload, { movedToJourney = false } = {}) => {
    if (movedToJourney) return 'Step moved to selected journey'

    const hasName = apiPayload.name != null
    const hasDescription = apiPayload.description != null
    const hasColor = apiPayload.color != null

    if ([hasName, hasDescription, hasColor].filter(Boolean).length > 1) {
        return 'Step details saved'
    }
    if (hasName) return 'Step name updated'
    if (hasDescription) return 'Step description saved'
    if (hasColor) return 'Step color updated'
    return 'Step details saved'
}

const getReleaseUpdateMessage = (apiPayload) => {
    const hasName = apiPayload.name != null
    const hasDescription = apiPayload.description != null
    const hasDates =
        apiPayload.start !== undefined || apiPayload.end !== undefined

    if ([hasName, hasDescription, hasDates].filter(Boolean).length > 1) {
        return 'Release details saved'
    }
    if (hasName) return 'Release name updated'
    if (hasDescription) return 'Release description saved'
    if (hasDates) return 'Release dates updated'
    return 'Release details saved'
}

const getStoryMoveMessage = (apiPayload) => {
    const hasStep = apiPayload.stepId != null
    const hasRelease = apiPayload.releaseId != null

    if (hasStep && hasRelease) {
        return 'Story moved to selected step and release'
    }
    if (hasRelease) return 'Story moved to selected release'
    if (hasStep) return 'Story moved to selected step'
    return 'Story location updated'
}

const getStoryUpdateMessage = (apiPayload) => {
    const hasName = apiPayload.name != null
    const hasDescription = apiPayload.description != null
    const hasColor = apiPayload.color != null
    const hasStatus = apiPayload.status != null

    if ([hasName, hasDescription, hasColor, hasStatus].filter(Boolean).length > 1) {
        return 'Story details saved'
    }
    if (hasName) return 'Story title updated'
    if (hasDescription) return 'Story description saved'
    if (hasColor) return 'Story color updated'
    if (hasStatus) return 'Story status updated'
    return 'Story details saved'
}

const cardExistsOnBoard = (boardData, selected) => {
    if (!boardData || !selected?.id) return false
    const normalizedId = String(selected.id)

    if (selected.type === 'journey') {
        return boardData.journeys?.some(
            (item) => String(item.id) === normalizedId
        )
    }

    if (selected.type === 'step') {
        return boardData.journeys?.some((journey) =>
            journey.steps?.some((step) => String(step.id) === normalizedId)
        )
    }

    if (selected.type === 'release') {
        return boardData.releases?.some(
            (item) => String(item.id) === normalizedId
        )
    }

    if (selected.type === 'story') {
        return boardData.releases?.some((release) =>
            release.stories?.some((story) => String(story.id) === normalizedId)
        )
    }

    return false
}

const useStoryMapBoard = (
    initialBoard,
    projectId,
    { initialDeepLink, onDeepLinkHandled, readOnly = false, canDelete = false } = {}
) => {
    const { userProfile } = useSelector(loginState)
    const readOnlyRef = useRef(readOnly)
    readOnlyRef.current = readOnly
    const canDeleteRef = useRef(canDelete)
    canDeleteRef.current = canDelete
    const [board, setBoard] = useState(initialBoard)
    const boardRef = useRef(initialBoard)
    const projectIdRef = useRef(projectId)
    const creatingJourneyRef = useRef(false)
    const creatingStepRef = useRef(false)
    const creatingReleaseRef = useRef(false)
    const creatingStoryRef = useRef(false)
    const [activeDragType, setActiveDragType] = useState(null)
    const [draggingStoryReleaseId, setDraggingStoryReleaseId] = useState(null)
    const [storyDragOverCellKey, setStoryDragOverCellKey] = useState(null)
    const [draggingStorySourceCellKey, setDraggingStorySourceCellKey] = useState(null)
    const storyDragOverCellKeyRef = useRef(null)
    const storyDragDropTargetKeyRef = useRef(null)
    const draggingStorySourceCellKeyRef = useRef(null)
    const [draggingStepJourneyId, setDraggingStepJourneyId] = useState(null)
    const [stepDragPlaceholder, setStepDragPlaceholder] = useState(null)
    const [editingJourneyId, setEditingJourneyId] = useState(null)
    const [editingJourneyDraft, setEditingJourneyDraft] = useState('')
    const [editingStepId, setEditingStepId] = useState(null)
    const [editingStepDraft, setEditingStepDraft] = useState('')
    const [editingStoryId, setEditingStoryId] = useState(null)
    const [editingStoryDraft, setEditingStoryDraft] = useState('')
    const [selectedCard, setSelectedCard] = useState(null)
    const [highlightedCardId, setHighlightedCardId] = useState(null)
    const [missingDeepLinkCard, setMissingDeepLinkCard] = useState(null)
    const [deleteConfirm, setDeleteConfirm] = useState(null)
    const [boardFilters, setBoardFilters] = useState(EMPTY_STORY_MAP_BOARD_FILTERS)
    const [filterLoading, setFilterLoading] = useState(false)
    const [filterMatchIndex, setFilterMatchIndex] = useState(0)
    const [filterFocusedCardId, setFilterFocusedCardId] = useState(null)
    const filterMatchIndexRef = useRef(0)
    const boardFiltersRef = useRef(EMPTY_STORY_MAP_BOARD_FILTERS)
    const deepLinkHandledRef = useRef(null)
    const topHScrollRef = useRef(null)
    const topPaneRef = useRef(null)
    const bodyPaneRef = useRef(null)
    const journeyInputRef = useRef(null)
    const stepInputRef = useRef(null)
    const storyInputRef = useRef(null)
    const editingJourneyDraftRef = useRef('')
    const editingStepDraftRef = useRef('')
    const editingStoryDraftRef = useRef('')
    const editingJourneyIdRef = useRef(null)
    const editingStepIdRef = useRef(null)
    const editingStoryIdRef = useRef(null)
    const skipStepBlurCommitRef = useRef(false)
    const skipStoryBlurCommitRef = useRef(false)
    const isSyncingScroll = useRef(false)
    const pendingCreateEditsRef = useRef(new Map())
    const pendingIdPromisesRef = useRef(new Map())
    const dragPersistQueueRef = useRef(Promise.resolve())
    const dragOverflowRestoreRef = useRef([])
    const isDragActiveRef = useRef(false)
    const dragPointerRef = useRef({ x: 0, y: 0 })
    const dragPointerCleanupRef = useRef(null)

    const registerPendingId = useCallback((tempId) => {
        const key = String(tempId)
        let resolveFn
        const promise = new Promise((resolve) => {
            resolveFn = resolve
        })
        pendingIdPromisesRef.current.set(key, { promise, resolve: resolveFn })
        return promise
    }, [])

    const resolvePendingId = useCallback((tempId, realId) => {
        const key = String(tempId)
        const entry = pendingIdPromisesRef.current.get(key)
        if (entry) {
            entry.resolve(realId)
            pendingIdPromisesRef.current.delete(key)
        }
    }, [])

    const failPendingId = useCallback((tempId) => {
        const key = String(tempId)
        const entry = pendingIdPromisesRef.current.get(key)
        if (entry) {
            entry.resolve(null)
            pendingIdPromisesRef.current.delete(key)
        }
        pendingCreateEditsRef.current.delete(key)
    }, [])

    const awaitRealId = useCallback(async (id) => {
        const key = String(id ?? '')
        if (isMongoObjectId(key)) return key
        const entry = pendingIdPromisesRef.current.get(key)
        if (!entry) return null
        return entry.promise
    }, [])

    const migrateEditingId = useCallback((kind, tempId, realId) => {
        const normalizedTemp = String(tempId)
        const normalizedReal = String(realId)

        if (
            kind === 'journey' &&
            editingJourneyIdRef.current === normalizedTemp
        ) {
            editingJourneyIdRef.current = normalizedReal
            setEditingJourneyId(normalizedReal)
        }
        if (kind === 'step' && editingStepIdRef.current === normalizedTemp) {
            editingStepIdRef.current = normalizedReal
            setEditingStepId(normalizedReal)
        }
        if (kind === 'story' && editingStoryIdRef.current === normalizedTemp) {
            editingStoryIdRef.current = normalizedReal
            setEditingStoryId(normalizedReal)
        }
    }, [])

    const clearEditingIfMatches = useCallback((kind, tempId) => {
        const normalizedTemp = String(tempId)

        if (kind === 'journey' && editingJourneyIdRef.current === normalizedTemp) {
            setEditingJourneyId(null)
            editingJourneyIdRef.current = null
            editingJourneyDraftRef.current = ''
            setEditingJourneyDraft('')
        }
        if (kind === 'step' && editingStepIdRef.current === normalizedTemp) {
            setEditingStepId(null)
            editingStepIdRef.current = null
            editingStepDraftRef.current = ''
            setEditingStepDraft('')
        }
        if (kind === 'story' && editingStoryIdRef.current === normalizedTemp) {
            setEditingStoryId(null)
            editingStoryIdRef.current = null
            editingStoryDraftRef.current = ''
            setEditingStoryDraft('')
        }
    }, [])

    const flushPendingCreateEdit = useCallback(async (tempId, realId) => {
        const pending = pendingCreateEditsRef.current.get(String(tempId))
        if (!pending) return

        pendingCreateEditsRef.current.delete(String(tempId))

        let result
        switch (pending.type) {
            case 'journey':
                result = await updateJourney(String(realId), { name: pending.name })
                break
            case 'step': {
                const realJourneyId = await awaitRealId(pending.journeyId)
                if (!realJourneyId) return
                result = await updateStep(String(realId), {
                    name: pending.name,
                    journeyId: realJourneyId,
                })
                break
            }
            case 'story': {
                const realReleaseId = await awaitRealId(pending.releaseId)
                if (!realReleaseId) return
                result = await updateStory(String(realId), {
                    name: pending.name,
                    releaseId: realReleaseId,
                })
                break
            }
            default:
                return
        }

        if (result && !result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to save title',
            })
        }
    }, [awaitRealId])

    useEffect(() => {
        projectIdRef.current = projectId
    }, [projectId])

    useEffect(() => {
        editingJourneyIdRef.current = editingJourneyId
    }, [editingJourneyId])

    useEffect(() => {
        editingStepIdRef.current = editingStepId
    }, [editingStepId])

    useEffect(() => {
        editingStoryIdRef.current = editingStoryId
    }, [editingStoryId])

    useEffect(() => {
        boardRef.current = board
    }, [board])

    useEffect(() => {
        setBoard(initialBoard)
        boardRef.current = initialBoard
    }, [initialBoard])

    const updateJourneyDraft = useCallback((value) => {
        editingJourneyDraftRef.current = value
        setEditingJourneyDraft(value)
    }, [])

    const updateStepDraft = useCallback((value) => {
        editingStepDraftRef.current = value
        setEditingStepDraft(value)
    }, [])

    const updateStoryDraft = useCallback((value) => {
        editingStoryDraftRef.current = value
        setEditingStoryDraft(value)
    }, [])

    const syncHorizontalScroll = useCallback((source) => {
        if (!source || isSyncingScroll.current) return

        isSyncingScroll.current = true
        const { scrollLeft } = source

        if (topHScrollRef.current && topHScrollRef.current !== source) {
            topHScrollRef.current.scrollLeft = scrollLeft
        }
        bodyPaneRef.current
            ?.querySelectorAll(`.${styles.releaseGridViewport}`)
            .forEach((viewport) => {
                if (viewport !== source) {
                    viewport.scrollLeft = scrollLeft
                }
            })

        requestAnimationFrame(() => {
            isSyncingScroll.current = false
        })
    }, [])

    const updateHorizontalScrollState = useCallback(() => {
        // Inline overflowX fights drag-time CSS (overflow: visible !important).
        if (isDragActiveRef.current) return

        const topEl = topHScrollRef.current
        if (!topEl) return

        const hasOverflow = topEl.scrollWidth > topEl.clientWidth + 1
        const overflowValue = hasOverflow ? 'auto' : 'hidden'

        topEl.style.overflowX = overflowValue
        topEl.dataset.canScroll = hasOverflow ? 'true' : 'false'

        bodyPaneRef.current
            ?.querySelectorAll(`.${styles.releaseGridViewport}`)
            .forEach((viewport) => {
                viewport.style.overflowX = overflowValue
            })

        if (!hasOverflow) {
            topEl.scrollLeft = 0
            bodyPaneRef.current
                ?.querySelectorAll(`.${styles.releaseGridViewport}`)
                .forEach((viewport) => {
                    viewport.scrollLeft = 0
                })
        }
    }, [])

    const clearInlineOverflowForDrag = useCallback(() => {
        const nodes = []
        if (topHScrollRef.current) nodes.push(topHScrollRef.current)
        bodyPaneRef.current
            ?.querySelectorAll(`.${styles.releaseGridViewport}`)
            .forEach((viewport) => nodes.push(viewport))

        dragOverflowRestoreRef.current = nodes.map((el) => ({
            el,
            overflowX: el.style.overflowX,
            overflowY: el.style.overflowY,
        }))

        nodes.forEach((el) => {
            el.style.overflowX = ''
            el.style.overflowY = ''
        })
    }, [])

    const restoreInlineOverflowAfterDrag = useCallback(() => {
        dragOverflowRestoreRef.current.forEach(({ el, overflowX, overflowY }) => {
            if (!el) return
            el.style.overflowX = overflowX
            el.style.overflowY = overflowY
        })
        dragOverflowRestoreRef.current = []
        updateHorizontalScrollState()
    }, [updateHorizontalScrollState])

    const stopDragPointerTracking = useCallback(() => {
        dragPointerCleanupRef.current?.()
        dragPointerCleanupRef.current = null
    }, [])

    const startDragPointerTracking = useCallback(() => {
        stopDragPointerTracking()
        const onMove = (event) => {
            dragPointerRef.current = { x: event.clientX, y: event.clientY }
        }
        // Seed from latest known position; keep listening for the whole drag.
        window.addEventListener('pointermove', onMove, { passive: true })
        window.addEventListener('mousemove', onMove, { passive: true })
        dragPointerCleanupRef.current = () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('mousemove', onMove)
        }
    }, [stopDragPointerTracking])

    // Keep a continuous pointer seed so drop works even on quick release.
    useEffect(() => {
        const onMove = (event) => {
            dragPointerRef.current = { x: event.clientX, y: event.clientY }
        }
        window.addEventListener('pointermove', onMove, { passive: true })
        return () => window.removeEventListener('pointermove', onMove)
    }, [])

    const resolveStoryCellFromPoint = useCallback((clientX, clientY) => {
        if (typeof document === 'undefined') return null
        if (
            !Number.isFinite(clientX) ||
            !Number.isFinite(clientY) ||
            (clientX === 0 && clientY === 0)
        ) {
            return null
        }

        const pointInRect = (rect, pad = 0) =>
            clientX >= rect.left - pad &&
            clientX <= rect.right + pad &&
            clientY >= rect.top - pad &&
            clientY <= rect.bottom + pad

        const toCell = (cell) => {
            const releaseId = cell.getAttribute('data-story-map-release-id')
            const stepId = cell.getAttribute('data-story-map-step-id')
            if (!releaseId || !stepId) return null
            return { releaseId, stepId, cellEl: cell }
        }

        // Prefer later (lower) release blocks when the pointer sits on a shared edge.
        const blocks = Array.from(
            document.querySelectorAll('[data-story-map-release-block]')
        )
        let matchedBlock = null
        for (let i = blocks.length - 1; i >= 0; i -= 1) {
            const rect = blocks[i].getBoundingClientRect()
            if (pointInRect(rect)) {
                matchedBlock = blocks[i]
                break
            }
        }

        const searchRoots = matchedBlock
            ? [matchedBlock]
            : [document]

        for (const root of searchRoots) {
            const cells = Array.from(
                root.querySelectorAll('[data-story-map-cell]')
            )

            for (const cell of cells) {
                if (pointInRect(cell.getBoundingClientRect())) {
                    const parsed = toCell(cell)
                    if (parsed) return parsed
                }
            }

            // Same column (X) in this release — allows dropping onto short/empty cells.
            let best = null
            let bestScore = Infinity
            for (const cell of cells) {
                const rect = cell.getBoundingClientRect()
                if (clientX < rect.left || clientX > rect.right) continue
                let score = 0
                if (clientY < rect.top) score = rect.top - clientY
                else if (clientY > rect.bottom) score = clientY - rect.bottom
                if (score < bestScore) {
                    bestScore = score
                    best = cell
                }
            }
            if (best) {
                const parsed = toCell(best)
                if (parsed) return parsed
            }
        }

        return null
    }, [])

    const resolveStoryIndexInCell = useCallback(
        (cellEl, clientY, storyCount, sourceCell, destCell, sourceIndex) => {
            if (!cellEl || storyCount <= 0) return 0

            const cards = Array.from(
                cellEl.querySelectorAll('[data-story-map-story-id]')
            )
            let index = cards.length
            for (let i = 0; i < cards.length; i += 1) {
                const rect = cards[i].getBoundingClientRect()
                if (clientY < rect.top + rect.height / 2) {
                    index = i
                    break
                }
            }

            if (
                sourceCell &&
                destCell &&
                isSameStoryCell(sourceCell, destCell) &&
                sourceIndex < index
            ) {
                index = Math.max(0, index - 1)
            }

            return Math.min(index, storyCount)
        },
        []
    )

    const resolveStepLaneFromPoint = useCallback((clientX, clientY) => {
        if (typeof document === 'undefined') return null
        const stack = document.elementsFromPoint(clientX, clientY)
        for (const el of stack) {
            const endLane = el.closest?.('[data-story-map-step-lane-end]')
            if (endLane) {
                return {
                    journeyId: endLane.getAttribute('data-story-map-step-lane-end'),
                    isEnd: true,
                    laneEl: endLane,
                }
            }
            const lane = el.closest?.('[data-story-map-step-lane]')
            if (lane) {
                return {
                    journeyId: lane.getAttribute('data-story-map-step-lane'),
                    isEnd: false,
                    laneEl: lane,
                }
            }
        }
        return null
    }, [])

    const resolveStepIndexInLane = useCallback(
        (laneEl, clientX, stepCount, sourceJourneyId, destJourneyId, sourceIndex) => {
            if (!laneEl || stepCount <= 0) return 0

            const cards = Array.from(
                laneEl.querySelectorAll('[data-story-map-step-id]')
            )
            let index = cards.length
            for (let i = 0; i < cards.length; i += 1) {
                const rect = cards[i].getBoundingClientRect()
                if (clientX < rect.left + rect.width / 2) {
                    index = i
                    break
                }
            }

            if (
                String(sourceJourneyId) === String(destJourneyId) &&
                sourceIndex < index
            ) {
                index = Math.max(0, index - 1)
            }

            return Math.min(index, stepCount)
        },
        []
    )

    const steps = useMemo(() => flattenSteps(board.journeys), [board.journeys])
    const boardTracks = useMemo(
        () => buildBoardTracks(board.journeys),
        [board.journeys]
    )
    const gridTemplateColumns = useMemo(
        () => buildGridTemplateColumns(boardTracks),
        [boardTracks]
    )

    useEffect(() => {
        const topEl = topHScrollRef.current
        if (!topEl) return undefined

        updateHorizontalScrollState()

        const observer = new ResizeObserver(updateHorizontalScrollState)
        observer.observe(topEl)

        window.addEventListener('resize', updateHorizontalScrollState)

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', updateHorizontalScrollState)
        }
    }, [updateHorizontalScrollState, boardTracks, board.releases.length])

    useEffect(() => {
        if (!editingJourneyId) return undefined

        const frame = requestAnimationFrame(() => {
            journeyInputRef.current?.focus()
            journeyInputRef.current?.select()
        })

        return () => cancelAnimationFrame(frame)
    }, [editingJourneyId])

    useEffect(() => {
        if (!editingStepId) return undefined

        const frame = requestAnimationFrame(() => {
            stepInputRef.current?.focus()
            stepInputRef.current?.select()
        })

        return () => cancelAnimationFrame(frame)
    }, [editingStepId])

    useEffect(() => {
        if (!editingStoryId) return undefined

        const frame = requestAnimationFrame(() => {
            storyInputRef.current?.focus()
            storyInputRef.current?.select()
        })

        return () => cancelAnimationFrame(frame)
    }, [editingStoryId])

    const handleHorizontalWheel = useCallback(
        (event) => {
            const topEl = topHScrollRef.current
            if (!topEl || topEl.dataset.canScroll !== 'true') return

            const deltaX = event.deltaX
            const deltaY = event.shiftKey ? event.deltaY : 0
            if (deltaX === 0 && deltaY === 0) return

            topEl.scrollLeft += deltaX !== 0 ? deltaX : deltaY
            syncHorizontalScroll(topEl)

            if (deltaX !== 0 || event.shiftKey) {
                event.preventDefault()
            }
        },
        [syncHorizontalScroll]
    )

    useEffect(() => {
        const topPane = topPaneRef.current
        const bodyPane = bodyPaneRef.current
        if (!topPane && !bodyPane) return undefined

        const wheelOptions = { passive: false }

        topPane?.addEventListener('wheel', handleHorizontalWheel, wheelOptions)
        bodyPane?.addEventListener('wheel', handleHorizontalWheel, wheelOptions)

        return () => {
            topPane?.removeEventListener('wheel', handleHorizontalWheel)
            bodyPane?.removeEventListener('wheel', handleHorizontalWheel)
        }
    }, [handleHorizontalWheel])

    const commitJourneyTitle = useCallback(async (journeyId, draft) => {
        if (readOnlyRef.current) return

        const name = resolveEditTitleName(
            draft,
            editingJourneyDraftRef,
            journeyInputRef,
            'Untitled journey'
        )

        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey, journeyIndex) => {
                if (journey.id !== journeyId) return journey
                return {
                    ...journey,
                    title: `${journeyIndex + 1}. ${name}`,
                }
            }),
        }))
        setEditingJourneyId(null)
        editingJourneyIdRef.current = null
        editingJourneyDraftRef.current = ''
        setEditingJourneyDraft('')

        if (!isMongoObjectId(journeyId)) {
            if (isTempId(journeyId)) {
                pendingCreateEditsRef.current.set(String(journeyId), {
                    type: 'journey',
                    name,
                })
            }
            return
        }

        const result = await updateJourney(String(journeyId), { name })
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to update journey',
            })
        }
    }, [])

    const findStepJourney = useCallback((stepId) => {
        const normalizedStepId = String(stepId ?? '')
        const journeys = boardRef.current?.journeys ?? []

        for (const journey of journeys) {
            const stepIndex = journey.steps.findIndex(
                (step) => String(step.id) === normalizedStepId
            )
            if (stepIndex >= 0) {
                return {
                    journeyId: journey.id,
                    journeyNumber: getJourneyNumber(journeys, journey.id),
                    stepIndex,
                }
            }
        }

        return null
    }, [])

    const commitStepTitle = useCallback(
        async (stepId, draft, { fromBlur = false } = {}) => {
            if (readOnlyRef.current) return

            if (fromBlur && skipStepBlurCommitRef.current) {
                skipStepBlurCommitRef.current = false
                return
            }

            const normalizedStepId = String(stepId ?? '')
            const name = resolveEditTitleName(
                draft,
                editingStepDraftRef,
                stepInputRef,
                'Untitled step'
            )
            const stepContext = findStepJourney(normalizedStepId)

            if (!stepContext) return

            skipStepBlurCommitRef.current = true

            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => {
                    if (String(journey.id) !== String(stepContext.journeyId)) {
                        return journey
                    }

                    return {
                        ...journey,
                        steps: journey.steps.map((step, idx) =>
                            String(step.id) === normalizedStepId
                                ? {
                                      ...step,
                                      title: `${stepContext.journeyNumber}.${idx + 1} ${name}`,
                                  }
                                : step
                        ),
                    }
                }),
            }))
            setEditingStepId(null)
            editingStepIdRef.current = null
            editingStepDraftRef.current = ''
            setEditingStepDraft('')

            requestAnimationFrame(() => {
                skipStepBlurCommitRef.current = false
            })

            if (
                !isMongoObjectId(normalizedStepId) ||
                !isMongoObjectId(stepContext.journeyId)
            ) {
                if (isTempId(normalizedStepId)) {
                    pendingCreateEditsRef.current.set(normalizedStepId, {
                        type: 'step',
                        name,
                        journeyId: stepContext.journeyId,
                    })
                }
                return
            }

            const result = await updateStep(normalizedStepId, {
                name,
                journeyId: String(stepContext.journeyId),
            })
            if (!result.success) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to update step',
                })
            }
        },
        [findStepJourney]
    )

    const findJourneyIdForStep = useCallback((stepId) => {
        const normalizedStepId = String(stepId ?? '')

        for (const journey of boardRef.current?.journeys ?? []) {
            if (journey.steps.some((step) => String(step.id) === normalizedStepId)) {
                return journey.id
            }
        }

        return null
    }, [])

    const findStoryRelease = useCallback((storyId) => {
        const normalizedStoryId = String(storyId ?? '')

        for (const release of boardRef.current?.releases ?? []) {
            const story = release.stories.find(
                (item) => String(item.id) === normalizedStoryId
            )
            if (story) {
                return {
                    releaseId: release.id,
                    story,
                }
            }
        }

        return null
    }, [])

    const commitStoryTitle = useCallback(
        async (storyId, draft, { fromBlur = false } = {}) => {
            if (readOnlyRef.current) return

            if (fromBlur && skipStoryBlurCommitRef.current) {
                skipStoryBlurCommitRef.current = false
                return
            }

            const normalizedStoryId = String(storyId ?? '')
            const name = resolveEditTitleName(
                draft,
                editingStoryDraftRef,
                storyInputRef,
                'Untitled story'
            )
            const storyContext = findStoryRelease(normalizedStoryId)

            if (!storyContext) return

            skipStoryBlurCommitRef.current = true

            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((release) => {
                    if (String(release.id) !== String(storyContext.releaseId)) {
                        return release
                    }

                    return {
                        ...release,
                        stories: release.stories.map((story) =>
                            String(story.id) === normalizedStoryId
                                ? { ...story, title: name }
                                : story
                        ),
                    }
                }),
            }))
            setEditingStoryId(null)
            editingStoryIdRef.current = null
            editingStoryDraftRef.current = ''
            setEditingStoryDraft('')

            requestAnimationFrame(() => {
                skipStoryBlurCommitRef.current = false
            })

            if (
                !isMongoObjectId(normalizedStoryId) ||
                !isMongoObjectId(storyContext.releaseId)
            ) {
                if (isTempId(normalizedStoryId)) {
                    pendingCreateEditsRef.current.set(normalizedStoryId, {
                        type: 'story',
                        name,
                        releaseId: storyContext.releaseId,
                    })
                }
                return
            }

            const result = await updateStory(normalizedStoryId, {
                name,
                releaseId: String(storyContext.releaseId),
            })
            if (!result.success) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to update story',
                })
            }
        },
        [findStoryRelease]
    )

    const commitPendingTitles = () => {
        if (editingJourneyIdRef.current) {
            commitJourneyTitle(
                editingJourneyIdRef.current,
                journeyInputRef.current?.value ?? editingJourneyDraftRef.current
            )
        }
        if (editingStepIdRef.current) {
            commitStepTitle(
                editingStepIdRef.current,
                stepInputRef.current?.value ?? editingStepDraftRef.current
            )
        }
        if (editingStoryIdRef.current) {
            commitStoryTitle(
                editingStoryIdRef.current,
                storyInputRef.current?.value ?? editingStoryDraftRef.current
            )
        }
    }

    const commitActiveInlineEdit = useCallback(
        (event) => {
            const { target } = event

            const tryCommitInput = (inputRef, editingIdRef, commitFn, options) => {
                const input = inputRef.current
                const editingId = editingIdRef.current
                if (!input || !editingId) return false
                if (document.activeElement !== input) return false
                if (input === target || input.contains(target)) return false

                commitFn(editingId, input.value, options)
                return true
            }

            if (
                tryCommitInput(
                    stepInputRef,
                    editingStepIdRef,
                    commitStepTitle,
                    { fromBlur: false }
                )
            ) {
                return true
            }

            if (
                tryCommitInput(
                    journeyInputRef,
                    editingJourneyIdRef,
                    commitJourneyTitle
                )
            ) {
                return true
            }

            if (
                tryCommitInput(
                    storyInputRef,
                    editingStoryIdRef,
                    commitStoryTitle,
                    { fromBlur: false }
                )
            ) {
                return true
            }

            return false
        },
        [commitJourneyTitle, commitStepTitle, commitStoryTitle]
    )

    const handleBoardMouseDown = useCallback(
        (event) => {
            commitActiveInlineEdit(event)
        },
        [commitActiveInlineEdit]
    )

    useEffect(() => {
        const handleDocumentPointerDown = (event) => {
            commitActiveInlineEdit(event)
        }

        document.addEventListener('pointerdown', handleDocumentPointerDown, true)
        return () => {
            document.removeEventListener(
                'pointerdown',
                handleDocumentPointerDown,
                true
            )
        }
    }, [commitActiveInlineEdit])

    const startStepEdit = (stepId) => {
        const normalizedStepId = String(stepId)
        setEditingStepId(normalizedStepId)
        editingStepIdRef.current = normalizedStepId
        setEditingStepDraft('')
        editingStepDraftRef.current = ''
    }

    const startStoryEdit = (storyId) => {
        const normalizedStoryId = String(storyId)
        setEditingStoryId(normalizedStoryId)
        editingStoryIdRef.current = normalizedStoryId
        setEditingStoryDraft('')
        editingStoryDraftRef.current = ''
    }

    const insertJourneyAt = async (index) => {
        if (readOnlyRef.current) return

        if (creatingJourneyRef.current) return

        commitPendingTitles()

        const storyMapId = boardRef.current?.id
        if (!storyMapId) return

        const tempId = createUid('journey')
        registerPendingId(tempId)
        creatingJourneyRef.current = true

        setBoard((prev) => {
            const journeys = [...prev.journeys]
            journeys.splice(index, 0, {
                id: tempId,
                title: `${index + 1}. `,
                steps: [],
            })
            return {
                ...prev,
                journeys: journeys.map((journey, journeyIndex) => ({
                    ...journey,
                    title:
                        journey.id === tempId
                            ? `${journeyIndex + 1}. `
                            : `${journeyIndex + 1}. ${journey.title.replace(/^\d+\.\s*/, '')}`,
                })),
            }
        })
        editingJourneyDraftRef.current = ''
        setEditingJourneyId(tempId)
        editingJourneyIdRef.current = tempId
        setEditingJourneyDraft('')
        creatingJourneyRef.current = false

        const result = await createJourney({
            storyMapId,
            name: '',
        })

        if (!result.success || !result.result?._id) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys
                    .filter((journey) => journey.id !== tempId)
                    .map((journey, journeyIndex) => ({
                        ...journey,
                        title: `${journeyIndex + 1}. ${journey.title.replace(/^\d+\.\s*/, '')}`,
                    })),
            }))
            clearEditingIfMatches('journey', tempId)
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create journey',
            })
            return
        }

        const realId = String(result.result._id)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) =>
                journey.id === tempId ? { ...journey, id: realId } : journey
            ),
        }))
        resolvePendingId(tempId, realId)
        migrateEditingId('journey', tempId, realId)
        await flushPendingCreateEdit(tempId, realId)
    }

    const insertStepAt = async (journeyId, stepIndex) => {
        if (readOnlyRef.current) return

        if (creatingStepRef.current) return

        commitPendingTitles()

        const storyMapId = boardRef.current?.id
        if (!storyMapId || (!isMongoObjectId(journeyId) && !isTempId(journeyId))) {
            return
        }

        const tempId = createUid('step')
        registerPendingId(tempId)
        creatingStepRef.current = true

        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) => {
                if (String(journey.id) !== String(journeyId)) return journey
                const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                const nextSteps = [...journey.steps]
                nextSteps.splice(stepIndex, 0, {
                    id: tempId,
                    title: `${journeyNumber}.${stepIndex + 1} `,
                })
                return {
                    ...journey,
                    steps: nextSteps.map((step, idx) => ({
                        ...step,
                        title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
                    })),
                }
            }),
        }))
        startStepEdit(tempId)
        creatingStepRef.current = false

        const realJourneyId = await awaitRealId(journeyId)
        if (!realJourneyId) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => {
                    if (String(journey.id) !== String(journeyId)) return journey
                    const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                    const nextSteps = journey.steps.filter(
                        (step) => String(step.id) !== String(tempId)
                    )
                    return {
                        ...journey,
                        steps: nextSteps.map((step, idx) => ({
                            ...step,
                            title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
                        })),
                    }
                }),
            }))
            clearEditingIfMatches('step', tempId)
            showActionNotification({
                type: 'error',
                message: 'Failed to create step',
            })
            return
        }

        const result = await createStep({
            storyMapId,
            journeyId: realJourneyId,
            name: '',
            index: stepIndex + 1,
            setIndex: true,
        })

        if (!result.success || !extractPersistedId(result.result)) {
            failPendingId(tempId)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) => {
                    if (String(journey.id) !== String(journeyId)) return journey
                const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                    const nextSteps = journey.steps.filter(
                        (step) => String(step.id) !== String(tempId)
                    )
                return {
                    ...journey,
                        steps: nextSteps.map((step, idx) => ({
                            ...step,
                            title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
                        })),
                    }
                }),
            }))
            clearEditingIfMatches('step', tempId)
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create step',
            })
            return
        }

        const realId = extractPersistedId(result.result)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) => ({
                ...journey,
                steps: journey.steps.map((step) =>
                    String(step.id) === String(tempId)
                        ? { ...step, id: realId }
                        : step
                ),
            })),
        }))
        resolvePendingId(tempId, realId)
        migrateEditingId('step', tempId, realId)
        await flushPendingCreateEdit(tempId, realId)
    }

    const addStep = async (journeyId) => {
        if (readOnlyRef.current) return

        if (creatingStepRef.current) return

        commitPendingTitles()

        const storyMapId = boardRef.current?.id
        const journey = boardRef.current?.journeys?.find(
            (item) => String(item.id) === String(journeyId)
        )
        if (
            !storyMapId ||
            !journey ||
            (!isMongoObjectId(journeyId) && !isTempId(journeyId))
        ) {
            return
        }

        const tempId = createUid('step')
        registerPendingId(tempId)
        creatingStepRef.current = true

        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((item) => {
                if (String(item.id) !== String(journeyId)) return item
                const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                const stepIndex = item.steps.length + 1
                return {
                    ...item,
                    steps: [
                        ...item.steps,
                        {
                            id: tempId,
                            title: `${journeyNumber}.${stepIndex} `,
                        },
                    ],
                }
            }),
        }))
        startStepEdit(tempId)
        creatingStepRef.current = false

        const realJourneyId = await awaitRealId(journeyId)
        if (!realJourneyId) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((item) => {
                    if (String(item.id) !== String(journeyId)) return item
                    const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                    return {
                        ...item,
                        steps: item.steps
                            .filter((step) => String(step.id) !== String(tempId))
                            .map((step, idx) => ({
                                ...step,
                                title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
                            })),
                    }
                }),
            }))
            clearEditingIfMatches('step', tempId)
            showActionNotification({
                type: 'error',
                message: 'Failed to create step',
            })
            return
        }

        const result = await createStep({
            storyMapId,
            journeyId: realJourneyId,
            name: '',
            index: journey.steps.length + 1,
            setIndex: true,
        })

        if (!result.success || !extractPersistedId(result.result)) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((item) => {
                    if (String(item.id) !== String(journeyId)) return item
                    const journeyNumber = getJourneyNumber(prev.journeys, journeyId)
                    return {
                        ...item,
                        steps: item.steps
                            .filter((step) => String(step.id) !== String(tempId))
                            .map((step, idx) => ({
                                ...step,
                                title: `${journeyNumber}.${idx + 1} ${step.title.replace(/^\d+\.\d+\s*/, '')}`,
                            })),
                    }
                }),
            }))
            clearEditingIfMatches('step', tempId)
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create step',
            })
            return
        }

        const realId = extractPersistedId(result.result)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) => ({
                ...journey,
                steps: journey.steps.map((step) =>
                    String(step.id) === String(tempId)
                        ? { ...step, id: realId }
                        : step
                ),
            })),
        }))
        resolvePendingId(tempId, realId)
        migrateEditingId('step', tempId, realId)
        await flushPendingCreateEdit(tempId, realId)
    }

    const addRelease = async (afterIndex) => {
        if (readOnlyRef.current) return

        if (creatingReleaseRef.current) return

        commitPendingTitles()

        const storyMapId = boardRef.current?.id
        const currentProjectId = projectIdRef.current
        if (!storyMapId || !currentProjectId) return

        const insertIndex = afterIndex + 1
        const releaseName = `Release ${insertIndex + 1}`
        const tempId = createUid('release')
        registerPendingId(tempId)
        creatingReleaseRef.current = true

        setBoard((prev) => {
            const release = {
                id: tempId,
                title: releaseName,
                dateRange: '',
                isCollapsed: false,
                stories: [],
            }
            const releases = [...prev.releases]
            releases.splice(insertIndex, 0, release)
            return { ...prev, releases }
        })
        creatingReleaseRef.current = false

        window.setTimeout(() => {
            document
                .querySelector(
                    `[data-story-map-release-block="${CSS.escape(String(tempId))}"]`
                )
                ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
        }, 100)

        const result = await createRelease({
            name: releaseName,
            storyMapId,
            journeyId: '',
            projectId: currentProjectId,
            index: insertIndex,
        })

        if (!result.success || !extractPersistedId(result.result)) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.filter(
                    (release) => String(release.id) !== String(tempId)
                ),
            }))
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create release',
            })
            return
        }

        const realId = extractPersistedId(result.result)
        setBoard((prev) => ({
            ...prev,
            releases: prev.releases.map((release) =>
                String(release.id) === String(tempId)
                    ? { ...release, id: realId }
                    : release
            ),
        }))
        resolvePendingId(tempId, realId)

        // Re-scroll after temp → real id remount so the new release stays in view
        window.setTimeout(() => {
            document
                .querySelector(
                    `[data-story-map-release-block="${CSS.escape(String(realId))}"]`
                )
                ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
        }, 100)
    }

    const addStory = async (releaseId, stepId) => {
        if (readOnlyRef.current) return

        if (creatingStoryRef.current) return

        commitPendingTitles()

        const storyMapId = boardRef.current?.id
        const currentProjectId = projectIdRef.current
        const journeyId = findJourneyIdForStep(stepId)
        const release = boardRef.current?.releases?.find(
            (item) => String(item.id) === String(releaseId)
        )

        if (!storyMapId || !currentProjectId || !release || !journeyId) {
            return
        }

        const storiesInCell = release.stories.filter(
            (story) => String(story.stepId) === String(stepId)
        ).length

        const tempId = createUid('story')
        registerPendingId(tempId)
        creatingStoryRef.current = true

        setBoard((prev) => ({
            ...prev,
            releases: prev.releases.map((item) => {
                if (String(item.id) !== String(releaseId)) return item
                return {
                    ...item,
                    stories: [
                        ...item.stories,
                        {
                            id: tempId,
                            title: '',
                            stepId: String(stepId),
                            status: 'not_ready',
                        },
                    ],
                }
            }),
        }))
        startStoryEdit(tempId)
        creatingStoryRef.current = false

        const realReleaseId = await awaitRealId(releaseId)
        const realStepId = await awaitRealId(stepId)
        const realJourneyId = await awaitRealId(journeyId)

        if (!realReleaseId || !realStepId || !realJourneyId) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((item) => {
                    if (String(item.id) !== String(releaseId)) return item
                    return {
                        ...item,
                        stories: item.stories.filter(
                            (story) => String(story.id) !== String(tempId)
                        ),
                    }
                }),
            }))
            clearEditingIfMatches('story', tempId)
            showActionNotification({
                type: 'error',
                message: 'Failed to create story',
            })
            return
        }

        const result = await createStory({
            name: '',
            storyMapId,
            projectId: currentProjectId,
            releaseId: realReleaseId,
            stepId: realStepId,
            journeyId: realJourneyId,
            index: storiesInCell + 1,
        })

        if (!result.success || !extractPersistedId(result.result)) {
            failPendingId(tempId)
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((item) => {
                    if (String(item.id) !== String(releaseId)) return item
                    return {
                        ...item,
                        stories: item.stories.filter(
                            (story) => String(story.id) !== String(tempId)
                        ),
                    }
                }),
            }))
            clearEditingIfMatches('story', tempId)
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to create story',
            })
            return
        }

        const realId = extractPersistedId(result.result)
        setBoard((prev) => ({
            ...prev,
            releases: prev.releases.map((item) => ({
                ...item,
                stories: item.stories.map((story) =>
                    String(story.id) === String(tempId)
                        ? { ...story, id: realId }
                        : story
                ),
            })),
        }))
        resolvePendingId(tempId, realId)
        migrateEditingId('story', tempId, realId)
        await flushPendingCreateEdit(tempId, realId)
    }

    const handleDragStart = useCallback((start) => {
        if (readOnlyRef.current) return

        isDragActiveRef.current = true
        clearInlineOverflowForDrag()
        startDragPointerTracking()
        setActiveDragType(start.type)
        if (start.type === 'STORY') {
            const sourceCell = parseCellId(start.source.droppableId)
            setDraggingStoryReleaseId(sourceCell?.releaseId ?? null)
            const sourceKey = sourceCell
                ? `${sourceCell.releaseId}|${sourceCell.stepId}`
                : null
            draggingStorySourceCellKeyRef.current = sourceKey
            setDraggingStorySourceCellKey(sourceKey)
            // Don't highlight source — card should only appear under the cursor.
            storyDragOverCellKeyRef.current = null
            storyDragDropTargetKeyRef.current = null
            setStoryDragOverCellKey(null)
        }
        if (start.type === 'STEP') {
            const journeyId = parseStepLaneId(start.source.droppableId)
            setDraggingStepJourneyId(journeyId)
            if (journeyId) {
                setStepDragPlaceholder({
                    journeyId,
                    index: start.source.index,
                })
            }
        }
    }, [clearInlineOverflowForDrag, startDragPointerTracking])

    const handleDragUpdate = useCallback((update) => {
        if (readOnlyRef.current) return

        if (update.type === 'STORY') {
            const pointerCell = resolveStoryCellFromPoint(
                dragPointerRef.current.x,
                dragPointerRef.current.y
            )
            const rbdCell = update.destination
                ? parseCellId(update.destination.droppableId)
                : null
            const cell = pointerCell || rbdCell
            const key = cell ? `${cell.releaseId}|${cell.stepId}` : null
            const sourceKey = draggingStorySourceCellKeyRef.current
            storyDragOverCellKeyRef.current = key
            // Drop-here highlight only for a different release/step.
            if (key && key !== sourceKey) {
                storyDragDropTargetKeyRef.current = key
                setStoryDragOverCellKey(key)
            } else {
                // Returned to source / outside — cancel foreign drop target.
                storyDragDropTargetKeyRef.current = null
                setStoryDragOverCellKey(null)
            }
            return
        }

        if (update.type === 'STEP') {
            const pointerLane = resolveStepLaneFromPoint(
                dragPointerRef.current.x,
                dragPointerRef.current.y
            )
            const journeyId =
                pointerLane?.journeyId ||
                (update.destination
                    ? parseStepLaneEndId(update.destination.droppableId) ||
                      parseStepLaneId(update.destination.droppableId)
                    : null)

            if (!journeyId) {
                if (!update.destination) setStepDragPlaceholder(null)
                return
            }

            const destJourney = boardRef.current.journeys?.find(
                (journey) => String(journey.id) === String(journeyId)
            )
            const stepCount = destJourney?.steps?.length ?? 0
            const index = pointerLane?.isEnd
                ? stepCount
                : pointerLane?.laneEl
                  ? resolveStepIndexInLane(
                        pointerLane.laneEl,
                        dragPointerRef.current.x,
                        stepCount,
                        parseStepLaneId(update.source.droppableId),
                        journeyId,
                        update.source.index
                    )
                  : update.destination?.index ?? 0

            setStepDragPlaceholder({ journeyId, index })
        }
    }, [resolveStepLaneFromPoint, resolveStepIndexInLane, resolveStoryCellFromPoint])

    const scrollToFilterMatchIndex = useCallback((index) => {
        window.setTimeout(() => {
            const selector = getFilterMatchSelector()
            const nodes = Array.from(document.querySelectorAll(selector))
            if (!nodes.length) {
                setFilterMatchIndex(0)
                setFilterFocusedCardId(null)
                return
            }

            const clamped =
                ((index % nodes.length) + nodes.length) % nodes.length

            const current = nodes[clamped]
            setFilterFocusedCardId(getFilterMatchCardId(current))
            setFilterMatchIndex(clamped)
            filterMatchIndexRef.current = clamped

            current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            })
        }, 250)
    }, [])

    const goToNextFilterMatch = useCallback(() => {
        scrollToFilterMatchIndex(filterMatchIndexRef.current + 1)
    }, [scrollToFilterMatchIndex])

    const goToPrevFilterMatch = useCallback(() => {
        scrollToFilterMatchIndex(filterMatchIndexRef.current - 1)
    }, [scrollToFilterMatchIndex])

    const refetchBoard = useCallback(async () => {
        const storyMapId = boardRef.current?.id
        if (!storyMapId || !isMongoObjectId(storyMapId)) return

        const result = await getStoryMapDetail(
            String(storyMapId),
            boardFiltersRef.current
        )
        if (result.success && result.board) {
            setBoard(result.board)
            setSelectedCard((prev) =>
                prev && cardExistsOnBoard(result.board, prev) ? prev : null
            )
        }
    }, [])

    const applyBoardFilters = useCallback(async (nextFilters) => {
        const storyMapId = boardRef.current?.id
        if (!storyMapId || !isMongoObjectId(storyMapId)) return

        boardFiltersRef.current = nextFilters
        setBoardFilters(nextFilters)

        if (!isStoryMapBoardFilterRequestReady(nextFilters)) {
            return
        }

        setFilterLoading(true)

        try {
            const result = await getStoryMapDetail(String(storyMapId), nextFilters)

            if (!result.success || !result.board) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to apply filters',
                })
                return
            }

            setBoard(result.board)
            setSelectedCard((prev) =>
                prev && cardExistsOnBoard(result.board, prev) ? prev : null
            )

            setFilterMatchIndex(0)
            filterMatchIndexRef.current = 0
            if (result.board.isFilterActive) {
                scrollToFilterMatchIndex(0)
            } else {
                setFilterFocusedCardId(null)
            }
        } finally {
            setFilterLoading(false)
        }
    }, [scrollToFilterMatchIndex])

    const schedulePersistDragChange = useCallback(
        (apiCall) => {
            if (!apiCall) return

            const enqueuePersist = () => {
                dragPersistQueueRef.current = dragPersistQueueRef.current
                    .then(async () => {
                        const result = await apiCall()
                        if (!result?.success) {
                            showActionNotification({
                                type: 'error',
                                message: result.message || 'Failed to move card',
                            })
                            await refetchBoard()
                        }
                        return result
                    })
                    .catch(async (error) => {
                        showActionNotification({
                            type: 'error',
                            message:
                                error?.response?.data?.message ??
                                error?.message ??
                                'Failed to move card',
                        })
                        await refetchBoard()
                        return { success: false }
                    })
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(enqueuePersist)
            })
        },
        [refetchBoard]
    )

    const handleDragEnd = useCallback(
        (result) => {
        const pointer = { ...dragPointerRef.current }
        isDragActiveRef.current = false
        stopDragPointerTracking()
        setActiveDragType(null)
        setDraggingStoryReleaseId(null)
        setStoryDragOverCellKey(null)
        storyDragOverCellKeyRef.current = null
        storyDragDropTargetKeyRef.current = null
        setDraggingStorySourceCellKey(null)
        draggingStorySourceCellKeyRef.current = null
        setDraggingStepJourneyId(null)
        setStepDragPlaceholder(null)
        restoreInlineOverflowAfterDrag()

        if (readOnlyRef.current) return

        const { source, destination, draggableId, type } = result

        let persistApi = null

        if (type === 'RELEASE') {
            if (!destination) return
            if (source.index === destination.index) return

            setBoard((prev) => {
                const releases = [...prev.releases]
                const [moved] = releases.splice(source.index, 1)
                releases.splice(destination.index, 0, moved)
                return { ...prev, releases }
            })

            const releaseId = draggableId.replace(/^release-/, '')
            if (isMongoObjectId(releaseId)) {
                persistApi = () =>
                    updateStoryMapIndex(releaseId, {
                        module: 'release',
                        newIndex: destination.index,
                    })
            }

            schedulePersistDragChange(persistApi)
            return
        }

        if (type === 'STEP') {
            const sourceJourneyId = parseStepLaneId(source.droppableId)
            const pointerLane = resolveStepLaneFromPoint(pointer.x, pointer.y)
            const destEndJourneyId = pointerLane?.isEnd
                ? pointerLane.journeyId
                : destination
                  ? parseStepLaneEndId(destination.droppableId)
                  : null
            const destJourneyId =
                pointerLane?.journeyId ||
                destEndJourneyId ||
                (destination
                    ? parseStepLaneId(destination.droppableId)
                    : null)

            if (!sourceJourneyId || !destJourneyId) return

            const destJourney = boardRef.current.journeys?.find(
                (journey) => String(journey.id) === String(destJourneyId)
            )
            const stepCount = destJourney?.steps?.length ?? 0
            const destinationIndex = destEndJourneyId
                ? stepCount
                : pointerLane?.laneEl
                  ? resolveStepIndexInLane(
                        pointerLane.laneEl,
                        pointer.x,
                        stepCount,
                        sourceJourneyId,
                        destJourneyId,
                        source.index
                    )
                  : destination?.index ?? 0

            if (
                String(sourceJourneyId) === String(destJourneyId) &&
                !destEndJourneyId &&
                source.index === destinationIndex
            ) {
                return
            }

            const stepId = draggableId.replace(/^step-/, '')

            if (String(sourceJourneyId) === String(destJourneyId) && !destEndJourneyId) {
                setBoard((prev) => ({
                    ...prev,
                    journeys: prev.journeys.map((journey) => {
                        if (String(journey.id) !== String(destJourneyId)) {
                            return journey
                        }
                        const nextSteps = [...journey.steps]
                        const [moved] = nextSteps.splice(source.index, 1)
                        nextSteps.splice(destinationIndex, 0, moved)
                        return renumberJourneySteps(
                            { ...journey, steps: nextSteps },
                            prev.journeys
                        )
                    }),
                }))

                if (isMongoObjectId(stepId)) {
                    persistApi = () =>
                        updateStoryMapIndex(stepId, {
                            module: 'step',
                            newIndex: destinationIndex,
                        })
                }
            } else {
            setBoard((prev) => {
                let movedStep = null
                const journeysAfterRemove = prev.journeys.map((journey) => {
                    if (String(journey.id) !== String(sourceJourneyId)) {
                        return journey
                    }
                    const steps = journey.steps.filter((step) => {
                        if (String(step.id) === String(stepId)) {
                            movedStep = step
                            return false
                        }
                        return true
                    })
                    return renumberJourneySteps(
                        { ...journey, steps },
                        prev.journeys
                    )
                })

                if (!movedStep) return prev

                const journeysAfterInsert = journeysAfterRemove.map((journey) => {
                    if (String(journey.id) !== String(destJourneyId)) {
                        return journey
                    }
                    const nextSteps = [...journey.steps]
                        nextSteps.splice(destinationIndex, 0, movedStep)
                    return renumberJourneySteps(
                        { ...journey, steps: nextSteps },
                        journeysAfterRemove
                    )
                })

                return { ...prev, journeys: journeysAfterInsert }
            })

                if (isMongoObjectId(stepId) && isMongoObjectId(destJourneyId)) {
                    persistApi = () =>
                        updateStep(String(stepId), {
                            journeyId: String(destJourneyId),
                            index: destinationIndex,
                        })
                }
            }

            schedulePersistDragChange(persistApi)
            return
        }

        if (type !== 'STORY') return

        const sourceCell = parseCellId(source.droppableId)
        const pointerCell = resolveStoryCellFromPoint(pointer.x, pointer.y)
        const rbdDestCell = destination
            ? parseCellId(destination.droppableId)
            : null

        // Prefer last "Drop here" target (other release/step). Never fall back
        // to the source cell when a foreign target was chosen.
        const sourceKey = draggingStorySourceCellKeyRef.current
        const dropTargetKey = storyDragDropTargetKeyRef.current
        const lastOverKey = storyDragOverCellKeyRef.current
        const overKey =
            dropTargetKey ||
            (lastOverKey && lastOverKey !== sourceKey ? lastOverKey : null)
        let destCell = null
        let overCellEl = null
        if (overKey) {
            const [releaseId, stepId] = overKey.split('|')
            if (releaseId && stepId) {
                destCell = { releaseId, stepId }
                overCellEl = document.querySelector(
                    `[data-story-map-release-block="${CSS.escape(releaseId)}"] [data-story-map-cell][data-story-map-step-id="${CSS.escape(stepId)}"]`
                )
            }
        }
        if (!destCell && pointerCell) {
            destCell = {
                releaseId: pointerCell.releaseId,
                stepId: pointerCell.stepId,
            }
            overCellEl = pointerCell.cellEl
        }
        if (!destCell) destCell = rbdDestCell

        if (!sourceCell || !destCell) return

        const release = boardRef.current.releases?.find(
            (item) => String(item.id) === String(destCell.releaseId)
        )
        const storyCount =
            release?.stories?.filter(
                (story) => String(story.stepId) === String(destCell.stepId)
            ).length ?? 0

        const storyId = draggableId.replace(/^story-/, '')
        const isSameCell = isSameStoryCell(sourceCell, destCell)

        let destinationIndex
        if (overCellEl) {
            destinationIndex = resolveStoryIndexInCell(
                overCellEl,
                pointer.y,
                storyCount,
                sourceCell,
                destCell,
                source.index
            )
        } else if (
            isSameCell &&
            destination &&
            rbdDestCell &&
            isSameStoryCell(rbdDestCell, destCell)
        ) {
            destinationIndex = destination.index
        } else if (pointerCell?.cellEl) {
            destinationIndex = resolveStoryIndexInCell(
                pointerCell.cellEl,
                pointer.y,
                storyCount,
                sourceCell,
                destCell,
                source.index
            )
        } else {
            destinationIndex = destination?.index ?? storyCount
        }

        if (isSameCell) {
            if (source.index === destinationIndex) return

            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((item) => {
                    if (String(item.id) !== String(destCell.releaseId)) {
                        return item
                    }

                    return {
                        ...item,
                        stories: reorderStoriesInCell(
                            item.stories,
                            destCell.stepId,
                            source.index,
                            destinationIndex
                        ),
                    }
                }),
            }))

            if (isMongoObjectId(storyId)) {
                persistApi = () =>
                    updateStoryMapIndex(storyId, {
                        module: 'story',
                        newIndex: destinationIndex,
                    })
            }
        } else {
        setBoard((prev) => {
            let movedStory = null
            const releases = prev.releases.map((item) => {
                const stories = item.stories.filter((story) => {
                    if (String(story.id) === String(storyId)) {
                        movedStory = story
                        return false
                    }
                    return true
                })
                return { ...item, stories }
            })

            if (!movedStory) return prev

            const updatedStory = {
                ...movedStory,
                stepId: destCell.stepId,
            }

            return {
                ...prev,
                releases: releases.map((item) => {
                    if (String(item.id) !== String(destCell.releaseId)) {
                        return item
                    }
                    const stories = [...item.stories]
                    stories.splice(destinationIndex, 0, updatedStory)
                    return { ...item, stories }
                }),
            }
        })

            if (
                isMongoObjectId(storyId) &&
                isMongoObjectId(destCell.stepId) &&
                isMongoObjectId(destCell.releaseId)
            ) {
                persistApi = () =>
                    updateStory(String(storyId), {
                        stepId: String(destCell.stepId),
                        releaseId: String(destCell.releaseId),
                        index: destinationIndex,
                    })
            }
        }

        schedulePersistDragChange(persistApi)
    },
        [
            schedulePersistDragChange,
            restoreInlineOverflowAfterDrag,
            stopDragPointerTracking,
            resolveStoryCellFromPoint,
            resolveStoryIndexInCell,
            resolveStepLaneFromPoint,
            resolveStepIndexInLane,
        ]
    )

    const closeDeleteConfirm = useCallback(() => {
        setDeleteConfirm(null)
    }, [])

    const showDeleteConfirm = useCallback((config) => {
        setDeleteConfirm(config)
    }, [])

    const deleteJourneyById = useCallback(
        (journeyId) => {
            if (readOnlyRef.current || !canDeleteRef.current) return

            const normalizedId = String(journeyId)
            const journey = boardRef.current?.journeys?.find(
                (item) => String(item.id) === normalizedId
            )
            const stepIds = new Set(
                (journey?.steps ?? []).map((step) => String(step.id))
            )
            const storyIds = new Set()
            ;(boardRef.current?.releases ?? []).forEach((release) => {
                release.stories.forEach((story) => {
                    if (stepIds.has(String(story.stepId))) {
                        storyIds.add(String(story.id))
                    }
                })
            })

            showDeleteConfirm({
                title: 'Delete this journey?',
                message:
                    'This journey and its steps will be removed from the project. Stories in this journey will also be removed. This cannot be undone.',
                onConfirm: async () => {
                    if (isMongoObjectId(normalizedId)) {
                        const result = await deleteJourney(normalizedId)
                        if (!result.success) {
                            showActionNotification({
                                type: 'error',
                                message: result.message || 'Failed to delete journey',
                            })
                            return
                        }
                    }

                    setBoard((prev) => {
                        const remaining = prev.journeys.filter(
                            (item) => String(item.id) !== normalizedId
                        )
                        const journeys = remaining.map((item, journeyIndex) => {
                            const name = parseJourneyLabel(item.title)
                            return renumberJourneySteps(
                                { ...item, title: `${journeyIndex + 1}. ${name}` },
                                remaining
                            )
                        })

                        return {
                            ...prev,
                            journeys,
                            releases: prev.releases.map((release) => ({
                                ...release,
                                stories: release.stories.filter(
                                    (story) => !stepIds.has(String(story.stepId))
                                ),
                            })),
                        }
                    })

                    setSelectedCard((prev) => {
                        if (!prev) return prev
                        if (
                            prev.type === 'journey' &&
                            String(prev.id) === normalizedId
                        ) {
                            return null
                        }
                        if (prev.type === 'step' && stepIds.has(String(prev.id))) {
                            return null
                        }
                        if (prev.type === 'story' && storyIds.has(String(prev.id))) {
                            return null
                        }
                        return prev
                    })

                    clearEditingIfMatches('journey', normalizedId)
                    stepIds.forEach((stepId) => {
                        clearEditingIfMatches('step', stepId)
                    })
                    storyIds.forEach((storyId) => {
                        clearEditingIfMatches('story', storyId)
                    })

                    closeDeleteConfirm()
                },
            })
        },
        [clearEditingIfMatches, closeDeleteConfirm, showDeleteConfirm]
    )

    const deleteStepById = useCallback(
        (stepId) => {
            if (readOnlyRef.current || !canDeleteRef.current) return

            const normalizedId = String(stepId)
            const stepContext = findStepJourney(normalizedId)
            if (!stepContext) return

            const storyIds = new Set()
            ;(boardRef.current?.releases ?? []).forEach((release) => {
                release.stories.forEach((story) => {
                    if (String(story.stepId) === normalizedId) {
                        storyIds.add(String(story.id))
                    }
                })
            })

            showDeleteConfirm({
                title: 'Delete this step?',
                message:
                    'This step will be removed from the project. Stories linked to this step will also be removed. This cannot be undone.',
                onConfirm: async () => {
                    if (isMongoObjectId(normalizedId)) {
                        const result = await deleteStep(normalizedId)
                        if (!result.success) {
                            showActionNotification({
                                type: 'error',
                                message: result.message || 'Failed to delete step',
                            })
                            return
                        }
                    }

                    setBoard((prev) => {
                        const journeys = prev.journeys.map((item) => {
                            if (String(item.id) !== String(stepContext.journeyId)) {
                                return item
                            }
                            return {
                                ...item,
                                steps: item.steps.filter(
                                    (s) => String(s.id) !== normalizedId
                                ),
                            }
                        })

                        return {
                            ...prev,
                            journeys: journeys.map((item) =>
                                renumberJourneySteps(item, journeys)
                            ),
                            releases: prev.releases.map((release) => ({
                                ...release,
                                stories: release.stories.filter(
                                    (story) => String(story.stepId) !== normalizedId
                                ),
                            })),
                        }
                    })

                    setSelectedCard((prev) => {
                        if (!prev) return prev
                        if (prev.type === 'step' && String(prev.id) === normalizedId) {
                            return null
                        }
                        if (prev.type === 'story' && storyIds.has(String(prev.id))) {
                            return null
                        }
                        return prev
                    })

                    clearEditingIfMatches('step', normalizedId)
                    storyIds.forEach((storyId) => {
                        clearEditingIfMatches('story', storyId)
                    })

                    closeDeleteConfirm()
                },
            })
        },
        [clearEditingIfMatches, closeDeleteConfirm, findStepJourney, showDeleteConfirm]
    )

    const deleteStoryById = useCallback(
        (storyId) => {
            if (readOnlyRef.current || !canDeleteRef.current) return

            const normalizedId = String(storyId)

            showDeleteConfirm({
                title: 'Delete this story?',
                message:
                    'This story will be removed from the project. This cannot be undone.',
                onConfirm: async () => {
                    if (isMongoObjectId(normalizedId)) {
                        const result = await deleteStory(normalizedId)
                        if (!result.success) {
                            showActionNotification({
                                type: 'error',
                                message: result.message || 'Failed to delete story',
                            })
                            return
                        }
                    }

                    setBoard((prev) => ({
                        ...prev,
                        releases: prev.releases.map((release) => ({
                            ...release,
                            stories: release.stories.filter(
                                (story) => String(story.id) !== normalizedId
                            ),
                        })),
                    }))

                    setSelectedCard((prev) => {
                        if (
                            prev?.type === 'story' &&
                            String(prev.id) === normalizedId
                        ) {
                            return null
                        }
                        return prev
                    })

                    clearEditingIfMatches('story', normalizedId)
                    closeDeleteConfirm()
                },
            })
        },
        [clearEditingIfMatches, closeDeleteConfirm, showDeleteConfirm]
    )

    const deleteReleaseById = useCallback(
        (releaseId) => {
            if (readOnlyRef.current || !canDeleteRef.current) return

            const normalizedId = String(releaseId)
            const release = boardRef.current?.releases?.find(
                (item) => String(item.id) === normalizedId
            )
            const storyIds = new Set(
                (release?.stories ?? []).map((story) => String(story.id))
            )

            showDeleteConfirm({
                title: 'Delete this release?',
                message:
                    'This release and its stories will be removed from the project. This cannot be undone.',
                onConfirm: async () => {
                    if (isMongoObjectId(normalizedId)) {
                        const result = await deleteRelease(normalizedId)
                        if (!result.success) {
                            showActionNotification({
                                type: 'error',
                                message: result.message || 'Failed to delete release',
                            })
                            return
                        }
                    }

                    setBoard((prev) => ({
                        ...prev,
                        releases: prev.releases.filter(
                            (item) => String(item.id) !== normalizedId
                        ),
                    }))

                    setSelectedCard((prev) => {
                        if (!prev) return prev
                        if (
                            prev.type === 'release' &&
                            String(prev.id) === normalizedId
                        ) {
                            return null
                        }
                        if (prev.type === 'story' && storyIds.has(String(prev.id))) {
                            return null
                        }
                        return prev
                    })

                    storyIds.forEach((storyId) => {
                        clearEditingIfMatches('story', storyId)
                    })

                    closeDeleteConfirm()
                },
            })
        },
        [clearEditingIfMatches, closeDeleteConfirm, showDeleteConfirm]
    )

    const journeyMenu = (journeyId) => {
        if (readOnly) return []

        const items = []

        if (canDelete) {
            items.push({
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: ({ domEvent }) => {
                    domEvent?.stopPropagation?.()
                    domEvent?.preventDefault?.()
                    deleteJourneyById(journeyId)
                },
            })
        }

        return items
    }

    const stepMenu = (stepId) => {
        const items = [
            {
                key: 'copy-link',
                label: 'Copy link',
                onClick: () => handleCopyCardLink('step', stepId),
            },
        ]

        if (!readOnly && canDelete) {
            items.push(
                { type: 'divider' },
                {
                    key: 'delete',
                    label: 'Delete',
                    danger: true,
                    onClick: () => deleteStepById(stepId),
                }
            )
        }

        return items
    }

    const storyMenu = (storyId) => {
        const items = [
            {
                key: 'copy-link',
                label: 'Copy link',
                onClick: () => handleCopyCardLink('story', storyId),
            },
        ]

        if (!readOnly && canDelete) {
            items.push(
                { type: 'divider' },
                {
                    key: 'delete',
                    label: 'Delete',
                    danger: true,
                    onClick: () => deleteStoryById(storyId),
                }
            )
        }

        return items
    }

    const releaseMenu = (releaseId) => {
        if (readOnly || !canDelete) return []

        return [
            {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: () => deleteReleaseById(releaseId),
            },
        ]
    }

    const openStepDetail = useCallback((stepId) => {
        setSelectedCard({ type: 'step', id: stepId })
    }, [])

    const openJourneyDetail = useCallback((journeyId) => {
        setSelectedCard({ type: 'journey', id: journeyId })
    }, [])

    const openReleaseDetail = useCallback((releaseId) => {
        setSelectedCard({ type: 'release', id: releaseId })
    }, [])

    const openStoryDetail = useCallback((storyId) => {
        setSelectedCard({ type: 'story', id: storyId })
    }, [])

    const handleCopyCardLink = useCallback(async (cardType, cardId) => {
        const link = buildStoryMapCardLink({
            projectId: projectIdRef.current,
            storyMapId: boardRef.current?.id,
            cardType,
            cardId,
        })

        if (!link) {
            showActionNotification({
                type: 'error',
                message: 'Save this card before copying a link',
            })
            return
        }

        const ok = await copyStoryMapLinkToClipboard(link)
        showActionNotification({
            type: ok ? 'success' : 'error',
            message: ok ? 'Link copied' : 'Could not copy link',
            notificationKey: 'story-map-card-link-copied',
        })
    }, [])

    const handleCopyBoardLink = useCallback(async () => {
        const link = buildStoryMapBoardLink({
            projectId: projectIdRef.current,
            storyMapId: boardRef.current?.id,
        })

        if (!link) {
            showActionNotification({
                type: 'error',
                message: 'Save this story map before copying a link',
            })
            return
        }

        const ok = await copyStoryMapLinkToClipboard(link)
        showActionNotification({
            type: ok ? 'success' : 'error',
            message: ok ? 'Link copied' : 'Could not copy link',
            notificationKey: 'story-map-board-link-copied',
        })
    }, [])

    useEffect(() => {
        deepLinkHandledRef.current = null
        setMissingDeepLinkCard(null)
    }, [initialDeepLink?.type, initialDeepLink?.id])

    useEffect(() => {
        if (!initialDeepLink?.id || !initialDeepLink?.type) return undefined

        const linkKey = `${initialDeepLink.type}:${initialDeepLink.id}`
        if (deepLinkHandledRef.current === linkKey) return undefined

        const normalizedId = String(initialDeepLink.id)

        if (!isMongoObjectId(normalizedId)) {
            deepLinkHandledRef.current = linkKey
            onDeepLinkHandled?.()
            return undefined
        }

        const cardExists =
            initialDeepLink.type === 'step'
                ? (board.journeys ?? []).some((journey) =>
                      journey.steps?.some(
                          (step) => String(step.id) === normalizedId
                      )
                  )
                : (board.releases ?? []).some((release) =>
                      release.stories?.some(
                          (story) => String(story.id) === normalizedId
                      )
                  )

        if (!cardExists) {
            deepLinkHandledRef.current = linkKey
            setMissingDeepLinkCard({
                type: initialDeepLink.type,
                id: normalizedId,
            })
            setSelectedCard(null)
            onDeepLinkHandled?.()
            return undefined
        }

        deepLinkHandledRef.current = linkKey

        if (initialDeepLink.type === 'step') {
            setSelectedCard({ type: 'step', id: normalizedId })
        } else {
            setSelectedCard({ type: 'story', id: normalizedId })
        }

        const scrollTimer = window.setTimeout(() => {
            const selector =
                initialDeepLink.type === 'step'
                    ? `[data-story-map-step-id="${normalizedId}"]`
                    : `[data-story-map-story-id="${normalizedId}"]`
            document.querySelector(selector)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            })
        }, 350)

        onDeepLinkHandled?.()

        return () => {
            window.clearTimeout(scrollTimer)
        }
    }, [
        board.journeys,
        board.releases,
        initialDeepLink,
        onDeepLinkHandled,
    ])

    const closeCardDetail = useCallback(() => {
        setSelectedCard(null)
    }, [])

    const closeMissingDeepLinkCard = useCallback(() => {
        setMissingDeepLinkCard(null)
    }, [])

    const updateStepCard = useCallback((stepId, updates) => {
        const normalizedStepId = String(stepId)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) => ({
                ...journey,
                steps: journey.steps.map((step) =>
                    String(step.id) === normalizedStepId
                        ? { ...step, ...updates }
                        : step
                ),
            })),
        }))
    }, [])

    const updateJourneyCard = useCallback((journeyId, updates) => {
        const normalizedJourneyId = String(journeyId)
        setBoard((prev) => ({
            ...prev,
            journeys: prev.journeys.map((journey) =>
                String(journey.id) === normalizedJourneyId
                    ? { ...journey, ...updates }
                    : journey
            ),
        }))
    }, [])

    const updateReleaseCard = useCallback((releaseId, updates) => {
        const normalizedReleaseId = String(releaseId)
        setBoard((prev) => ({
            ...prev,
            releases: prev.releases.map((release) =>
                String(release.id) === normalizedReleaseId
                    ? { ...release, ...updates }
                    : release
            ),
        }))
    }, [])

    const toggleReleaseCollapse = useCallback(async (releaseId) => {
        const release = boardRef.current?.releases?.find(
            (item) => String(item.id) === String(releaseId)
        )
        if (!release) return

        const nextCollapsed = !Boolean(release.isCollapsed)
        updateReleaseCard(releaseId, { isCollapsed: nextCollapsed })

        if (readOnlyRef.current || !isMongoObjectId(releaseId)) return

        const result = await updateRelease(String(releaseId), {
            isCollapsed: nextCollapsed,
        })

        if (!result.success) {
            updateReleaseCard(releaseId, { isCollapsed: !nextCollapsed })
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to update release',
            })
        }
    }, [updateReleaseCard])

    const updateStoryCard = useCallback((storyId, updates) => {
        const normalizedStoryId = String(storyId)
        setBoard((prev) => ({
            ...prev,
            releases: prev.releases.map((release) => ({
                ...release,
                stories: release.stories.map((story) =>
                    String(story.id) === normalizedStoryId
                        ? { ...story, ...updates }
                        : story
                ),
            })),
        }))
    }, [])

    const selectedCardDetail = useMemo(() => {
        if (!selectedCard) return null

        const navigation = buildBoardNavigationContext(
            board.journeys,
            board.releases
        )

        if (selectedCard.type === 'journey') {
            const journeyIndex = board.journeys.findIndex(
                (item) => String(item.id) === String(selectedCard.id)
            )
            const journey = board.journeys[journeyIndex]
            if (!journey) return null

            return {
                type: 'journey',
                card: journey,
                context: {
                    journeyIndex,
                    journeyId: journey.id,
                    stepsCount: journey.steps?.length ?? 0,
                    ...navigation,
                },
            }
        }

        if (selectedCard.type === 'step') {
            for (const journey of board.journeys) {
                const step = journey.steps.find((item) => item.id === selectedCard.id)
                if (step) {
                    return {
                        type: 'step',
                        card: step,
                        context: {
                            journeyTitle: parseJourneyLabel(journey.title),
                            journeyId: journey.id,
                            stepTitle: parseStepLabel(step.title).name,
                            stepId: step.id,
                            ...navigation,
                        },
                    }
                }
            }
            return null
        }

        if (selectedCard.type === 'release') {
            const releaseIndex = board.releases.findIndex(
                (item) => String(item.id) === String(selectedCard.id)
            )
            const release = board.releases[releaseIndex]
            if (!release) return null

            return {
                type: 'release',
                card: release,
                context: {
                    releaseId: release.id,
                    releaseIndex,
                    storiesCount: release.stories?.length ?? 0,
                    ...navigation,
                },
            }
        }

        for (const release of board.releases) {
            const story = release.stories.find((item) => item.id === selectedCard.id)
            if (story) {
                const flatSteps = flattenSteps(board.journeys)
                const linkedStep = flatSteps.find(
                    (step) => String(step.id) === String(story.stepId)
                )
                const linkedJourney = board.journeys.find((journey) =>
                    journey.steps.some(
                        (step) => String(step.id) === String(story.stepId)
                    )
                )
                const releaseIndex = board.releases.findIndex(
                    (item) => String(item.id) === String(release.id)
                )

                return {
                    type: 'story',
                    card: story,
                    context: {
                        releaseTitle: release.title || 'Release 1',
                        releaseId: release.id,
                        releaseIndex,
                        journeyTitle: linkedJourney
                            ? parseJourneyLabel(linkedJourney.title)
                            : linkedStep
                              ? parseJourneyLabel(linkedStep.journeyTitle)
                              : '',
                        journeyId: linkedJourney?.id ?? linkedStep?.journeyId ?? null,
                        stepTitle: linkedStep
                            ? parseStepLabel(linkedStep.title).name
                            : 'Untitled step',
                        stepId: story.stepId,
                        ...navigation,
                    },
                }
            }
        }

        return null
    }, [board.journeys, board.releases, selectedCard])

    const handleCardDetailUpdate = useCallback(
        async (cardId, updates) => {
            if (readOnlyRef.current) return
            if (!selectedCard) return

            if (selectedCard.type === 'journey') {
                updateJourneyCard(cardId, updates)

                if (!isMongoObjectId(cardId)) return

                const hasTitleUpdate = updates.title != null
                const hasDescriptionUpdate = updates.description != null

                if (!hasTitleUpdate && !hasDescriptionUpdate) return

                const journey = boardRef.current.journeys?.find(
                    (item) => String(item.id) === String(cardId)
                )
                if (!journey) return

                const name = String(
                    parseJourneyLabel(
                        hasTitleUpdate ? updates.title : journey.title
                    )
                ).trim() || 'Untitled journey'

                const currentName =
                    String(parseJourneyLabel(journey.title)).trim() ||
                    'Untitled journey'

                const apiPayload = {}

                if (hasTitleUpdate && name !== currentName) {
                    apiPayload.name = name
                }

                if (
                    hasDescriptionUpdate &&
                    !descriptionsAreEqual(
                        updates.description,
                        journey.description ?? ''
                    )
                ) {
                    apiPayload.description = updates.description
                }

                if (Object.keys(apiPayload).length === 0) return

                const result = await updateJourney(String(cardId), apiPayload)
                if (!result.success) {
                    showActionNotification({
                        type: 'error',
                        message: result.message || 'Failed to update journey',
                    })
                } else {
                    notifyDetailUpdateSuccess(
                        getJourneyUpdateMessage(apiPayload)
                    )
                }
                return
            }

            if (selectedCard.type === 'step') {
                const hasJourneyUpdate = updates.journeyId != null

                if (hasJourneyUpdate) {
                    const normalizedStepId = String(cardId)
                    const newJourneyId = String(updates.journeyId)

                    let sourceJourneyId = null
                    let movedStep = null
                    for (const journey of boardRef.current.journeys ?? []) {
                        const found = journey.steps.find(
                            (item) => String(item.id) === normalizedStepId
                        )
                        if (found) {
                            sourceJourneyId = journey.id
                            movedStep = found
                            break
                        }
                    }

                    if (
                        movedStep &&
                        sourceJourneyId &&
                        String(sourceJourneyId) !== newJourneyId
                    ) {
                        const destJourney = boardRef.current.journeys?.find(
                            (journey) => String(journey.id) === newJourneyId
                        )
                        const destinationIndex = destJourney?.steps?.length ?? 0

                        setBoard((prev) => {
                            let step = null
                            const journeysAfterRemove = prev.journeys.map((journey) => {
                                if (String(journey.id) !== String(sourceJourneyId)) {
                                    return journey
                                }

                                const steps = journey.steps.filter((item) => {
                                    if (String(item.id) === normalizedStepId) {
                                        step = item
                                        return false
                                    }
                                    return true
                                })

                                return renumberJourneySteps(
                                    { ...journey, steps },
                                    prev.journeys
                                )
                            })

                            if (!step) return prev

                            const journeysAfterInsert = journeysAfterRemove.map(
                                (journey) => {
                                    if (String(journey.id) !== newJourneyId) {
                                        return journey
                                    }

                                    const nextSteps = [...journey.steps, step]
                                    return renumberJourneySteps(
                                        { ...journey, steps: nextSteps },
                                        journeysAfterRemove
                                    )
                                }
                            )

                            return { ...prev, journeys: journeysAfterInsert }
                        })

                        if (
                            isMongoObjectId(normalizedStepId) &&
                            isMongoObjectId(newJourneyId)
                        ) {
                            const result = await updateStep(normalizedStepId, {
                                journeyId: newJourneyId,
                                index: destinationIndex,
                            })
                            if (!result.success) {
                                showActionNotification({
                                    type: 'error',
                                    message:
                                        result.message || 'Failed to move step',
                                })
                            } else {
                                notifyDetailUpdateSuccess(
                                    getStepUpdateMessage({}, {
                                        movedToJourney: true,
                                    })
                                )
                            }
                        }
                    }
                }

                const { journeyId: _journeyId, ...stepUpdates } = updates
                if (Object.keys(stepUpdates).length === 0) return

                updateStepCard(cardId, stepUpdates)

                if (!isMongoObjectId(cardId)) return

                const hasTitleUpdate = stepUpdates.title != null
                const hasDescriptionUpdate = stepUpdates.description != null
                const hasColorUpdate =
                    stepUpdates.color != null || stepUpdates.accentColor != null

                if (!hasTitleUpdate && !hasDescriptionUpdate && !hasColorUpdate) {
                    return
                }

                let journeyId = null
                let step = null
                for (const journey of boardRef.current.journeys ?? []) {
                    const found = journey.steps.find(
                        (item) => String(item.id) === String(cardId)
                    )
                    if (found) {
                        step = found
                        journeyId = journey.id
                        break
                    }
                }

                if (!journeyId || !isMongoObjectId(journeyId) || !step) return

                const name = String(
                    parseStepLabel(
                        hasTitleUpdate ? stepUpdates.title : step.title
                    ).name
                ).trim() || 'Untitled step'

                const currentName =
                    String(parseStepLabel(step.title).name).trim() ||
                    'Untitled step'

                const apiPayload = {
                    journeyId: String(journeyId),
                }

                if (hasTitleUpdate && name !== currentName) {
                    apiPayload.name = name
                }

                if (
                    hasDescriptionUpdate &&
                    !descriptionsAreEqual(
                        stepUpdates.description,
                        step.description ?? ''
                    )
                ) {
                    apiPayload.description = stepUpdates.description
                }

                if (hasColorUpdate) {
                    const nextColor = normalizeHexColor(
                        stepUpdates.color ?? stepUpdates.accentColor
                    )
                    const currentColor = normalizeHexColor(
                        step.color ?? step.accentColor
                    )
                    if (nextColor !== currentColor) {
                        apiPayload.color = nextColor
                    }
                }

                if (Object.keys(apiPayload).length <= 1) return

                const result = await updateStep(String(cardId), apiPayload)
                if (!result.success) {
                    showActionNotification({
                        type: 'error',
                        message: result.message || 'Failed to update step',
                    })
                } else {
                    notifyDetailUpdateSuccess(getStepUpdateMessage(apiPayload))
                }
                return
            }

            if (selectedCard.type === 'release') {
                updateReleaseCard(cardId, updates)

                if (!isMongoObjectId(cardId)) return

                const hasTitleUpdate = updates.title != null
                const hasDescriptionUpdate = updates.description != null
                const hasDateUpdate =
                    updates.start !== undefined || updates.end !== undefined

                if (!hasTitleUpdate && !hasDescriptionUpdate && !hasDateUpdate) {
                    return
                }

                const release = boardRef.current.releases?.find(
                    (item) => String(item.id) === String(cardId)
                )
                if (!release) return

                const apiPayload = {}

                if (hasTitleUpdate) {
                    const nextTitle =
                        String(updates.title ?? '').trim() || 'Untitled release'
                    const currentTitle =
                        String(release.title ?? '').trim() || 'Untitled release'
                    if (nextTitle !== currentTitle) {
                        apiPayload.name = nextTitle
                    }
                }

                if (
                    hasDescriptionUpdate &&
                    !descriptionsAreEqual(
                        updates.description,
                        release.description ?? ''
                    )
                ) {
                    apiPayload.description = updates.description
                }

                if (hasDateUpdate) {
                    const nextStart =
                        updates.start !== undefined
                            ? updates.start
                            : release.start
                    const nextEnd =
                        updates.end !== undefined ? updates.end : release.end
                    if (
                        nextStart !== release.start ||
                        nextEnd !== release.end
                    ) {
                        apiPayload.start = nextStart
                        apiPayload.end = nextEnd
                    }
                }

                if (Object.keys(apiPayload).length === 0) return

                const result = await updateRelease(String(cardId), apiPayload)
                if (!result.success) {
                    showActionNotification({
                        type: 'error',
                        message: result.message || 'Failed to update release',
                    })
                } else {
                    notifyDetailUpdateSuccess(
                        getReleaseUpdateMessage(apiPayload)
                    )
                }
                return
            }

            const hasStepUpdate = updates.stepId != null
            const hasReleaseUpdate = updates.releaseId != null

            if (hasStepUpdate || hasReleaseUpdate) {
                const storyContext = findStoryRelease(cardId)
                if (!storyContext) return

                const { story, releaseId: currentReleaseId } = storyContext
                const newStepId = hasStepUpdate
                    ? String(updates.stepId)
                    : String(story.stepId ?? '')
                const newReleaseId = hasReleaseUpdate
                    ? String(updates.releaseId)
                    : String(currentReleaseId)

                if (newReleaseId !== String(currentReleaseId)) {
                    setBoard((prev) => {
                        let movedStory = null
                        const releasesWithout = prev.releases.map((release) => ({
                            ...release,
                            stories: release.stories.filter((item) => {
                                if (String(item.id) === String(cardId)) {
                                    movedStory = { ...item, stepId: newStepId }
                                    return false
                                }
                                return true
                            }),
                        }))

                        if (!movedStory) return prev

                        return {
                            ...prev,
                            releases: releasesWithout.map((release) => {
                                if (String(release.id) !== newReleaseId) {
                                    return release
                                }

                                return {
                                    ...release,
                                    stories: [...release.stories, movedStory],
                                }
                            }),
                        }
                    })
                } else if (
                    hasStepUpdate &&
                    newStepId !== String(story.stepId ?? '')
                ) {
                    updateStoryCard(cardId, { stepId: newStepId })
                }

                if (isMongoObjectId(cardId)) {
                    const apiPayload = {}
                    if (
                        hasStepUpdate &&
                        newStepId !== String(story.stepId ?? '')
                    ) {
                        apiPayload.stepId = newStepId
                    }
                    if (
                        hasReleaseUpdate &&
                        newReleaseId !== String(currentReleaseId)
                    ) {
                        apiPayload.releaseId = newReleaseId
                    }

                    if (Object.keys(apiPayload).length === 0) return

                    const result = await updateStory(String(cardId), apiPayload)
                    if (!result.success) {
                        showActionNotification({
                            type: 'error',
                            message: result.message || 'Failed to update story',
                        })
                    } else {
                        notifyDetailUpdateSuccess(
                            getStoryMoveMessage(apiPayload)
                        )
                    }
                }

                return
            }

            updateStoryCard(cardId, updates)

            if (!isMongoObjectId(cardId)) return

            const storyContext = findStoryRelease(cardId)
            if (!storyContext || !isMongoObjectId(storyContext.releaseId)) return

            const { story, releaseId } = storyContext
            const hasTitleUpdate = updates.title != null
            const hasDescriptionUpdate = updates.description != null
            const hasColorUpdate =
                updates.color != null || updates.accentColor != null
            const hasStatusUpdate = updates.status != null

            if (
                !hasTitleUpdate &&
                !hasDescriptionUpdate &&
                !hasColorUpdate &&
                !hasStatusUpdate
            ) {
                return
            }

            const name =
                String(hasTitleUpdate ? updates.title : story.title).trim() ||
                'Untitled story'

            const currentName =
                String(story.title ?? '').trim() || 'Untitled story'

            const apiPayload = {
                releaseId: String(releaseId),
            }

            if (hasTitleUpdate && name !== currentName) {
                apiPayload.name = name
            }

            if (
                hasDescriptionUpdate &&
                !descriptionsAreEqual(
                    updates.description,
                    story.description ?? ''
                )
            ) {
                apiPayload.description = updates.description
            }

            if (hasColorUpdate) {
                const nextColor = normalizeHexColor(
                    updates.color ?? updates.accentColor
                )
                const currentColor = normalizeHexColor(
                    story.color ?? story.accentColor
                )
                if (nextColor !== currentColor) {
                    apiPayload.color = nextColor
                }
            }

            if (hasStatusUpdate && updates.status !== story.status) {
                apiPayload.status = toStoryStatusApiValue(updates.status)
            }

            if (Object.keys(apiPayload).length <= 1) return

            const result = await updateStory(String(cardId), apiPayload)
            if (!result.success) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to update story',
                })
            } else {
                notifyDetailUpdateSuccess(getStoryUpdateMessage(apiPayload))
            }
        },
        [selectedCard, findStoryRelease, updateJourneyCard, updateReleaseCard, updateStepCard, updateStoryCard]
    )

    const resolveCommentModuleType = (cardType) => {
        if (cardType === 'story') return 'story'
        if (cardType === 'journey') return 'journey'
        if (cardType === 'release') return 'release'
        return 'steps'
    }

    const handleAddComment = useCallback(async (cardId, cardType, content) => {
        const text = String(content ?? '').trim()
        if (!text) return { success: false }

        const storyMapId = boardRef.current?.id
        if (!storyMapId || !isMongoObjectId(cardId)) {
            return { success: false }
        }

        const moduleType = resolveCommentModuleType(cardType)
        const username =
            userProfile?.userName ||
            userProfile?.name ||
            userProfile?.fullName ||
            'Anonymous'

        const result = await addStoryMapComment({
            username,
            content: text,
            storyMapId: String(storyMapId),
            moduleId: String(cardId),
            moduleType,
        })

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to add comment',
            })
            return { success: false }
        }

        const commentId =
            extractPersistedId(result.result) || `comment-${Date.now()}`
        const apiContent = result.result?.content
        const hasApiContent =
            typeof apiContent === 'string' &&
            apiContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
        const newComment = {
            id: commentId,
            author: result.result?.username || username,
            // Prefer the text we submitted — API content is sometimes empty Quill HTML
            text: hasApiContent ? apiContent : text,
            createdAt: 'Just now',
        }

        if (cardType === 'journey') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => {
                    if (String(journey.id) !== String(cardId)) return journey
                    const prevComments = journey.comments || []
                    const nextComments = [...prevComments, newComment]
                    return {
                        ...journey,
                        comments: nextComments,
                        commentsCount: nextComments.length,
                        hasComments: true,
                        showCommentsIcon: true,
                    }
                }),
            }))
        } else if (cardType === 'release') {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((release) => {
                    if (String(release.id) !== String(cardId)) return release
                    const prevComments = release.comments || []
                    const nextComments = [...prevComments, newComment]
                    return {
                        ...release,
                        comments: nextComments,
                        commentsCount: nextComments.length,
                        hasComments: true,
                        showCommentsIcon: true,
                    }
                }),
            }))
        } else if (cardType === 'step') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => ({
                    ...journey,
                    steps: journey.steps.map((step) => {
                        if (String(step.id) !== String(cardId)) return step
                        const prevComments = step.comments || []
                        const nextComments = [...prevComments, newComment]
                        return {
                            ...step,
                            comments: nextComments,
                            commentsCount: nextComments.length,
                            hasComments: true,
                            showCommentsIcon: true,
                        }
                    }),
                })),
            }))
        } else {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((release) => ({
                    ...release,
                    stories: release.stories.map((story) => {
                        if (String(story.id) !== String(cardId)) return story
                        const prevComments = story.comments || []
                        const nextComments = [...prevComments, newComment]
                        return {
                            ...story,
                            comments: nextComments,
                            commentsCount: nextComments.length,
                            hasComments: true,
                            showCommentsIcon: true,
                        }
                    }),
                })),
            }))
        }

        return { success: true, comment: newComment }
    }, [userProfile])

    const handleDeleteComment = useCallback(async (cardId, cardType, commentId) => {
        if (readOnlyRef.current || !canDeleteRef.current) {
            return { success: false }
        }

        if (!isMongoObjectId(commentId)) {
            return { success: false }
        }

        const result = await deleteStoryMapComment(String(commentId))

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to delete comment',
            })
            return { success: false }
        }

        const patchCard = (card) => {
            if (String(card.id) !== String(cardId)) return card

            const prevComments = Array.isArray(card.comments)
                ? card.comments
                : null
            const nextComments = prevComments
                ? prevComments.filter(
                      (comment) => String(comment.id) !== String(commentId)
                  )
                : undefined
            const prevCount =
                typeof card.commentsCount === 'number'
                    ? card.commentsCount
                    : prevComments?.length ?? 0
            const nextCount = Math.max(0, prevCount - 1)

            return {
                ...card,
                ...(nextComments ? { comments: nextComments } : {}),
                commentsCount: nextCount,
                hasComments: nextCount > 0,
                showCommentsIcon: nextCount > 0,
            }
        }

        if (cardType === 'journey') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map(patchCard),
            }))
        } else if (cardType === 'release') {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map(patchCard),
            }))
        } else if (cardType === 'step') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => ({
                    ...journey,
                    steps: journey.steps.map(patchCard),
                })),
            }))
        } else {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((release) => ({
                    ...release,
                    stories: release.stories.map(patchCard),
                })),
            }))
        }

        showActionNotification({
            type: 'success',
            message: result.message || 'Comment deleted successfully',
        })

        return { success: true }
    }, [])

    const handleEditComment = useCallback(async (cardId, cardType, commentId, content) => {
        if (readOnlyRef.current) {
            return { success: false }
        }

        if (!isMongoObjectId(commentId)) {
            return { success: false }
        }

        const text = String(content ?? '').trim()
        if (!text) {
            return { success: false }
        }

        const username =
            userProfile?.userName ||
            userProfile?.name ||
            userProfile?.fullName ||
            undefined

        const result = await editStoryMapComment(String(commentId), {
            content: text,
            username,
        })

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to update comment',
            })
            return { success: false }
        }

        const apiContent = result.result?.content
        const hasApiContent =
            typeof apiContent === 'string' &&
            apiContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
        const nextText = hasApiContent ? apiContent : text

        const patchCard = (card) => {
            if (String(card.id) !== String(cardId)) return card
            if (!Array.isArray(card.comments)) return card

            return {
                ...card,
                comments: card.comments.map((comment) =>
                    String(comment.id) === String(commentId)
                        ? { ...comment, text: nextText }
                        : comment
                ),
            }
        }

        if (cardType === 'journey') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map(patchCard),
            }))
        } else if (cardType === 'release') {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map(patchCard),
            }))
        } else if (cardType === 'step') {
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => ({
                    ...journey,
                    steps: journey.steps.map(patchCard),
                })),
            }))
        } else {
            setBoard((prev) => ({
                ...prev,
                releases: prev.releases.map((release) => ({
                    ...release,
                    stories: release.stories.map(patchCard),
                })),
            }))
        }

        showActionNotification({
            type: 'success',
            message: result.message || 'Comment updated successfully',
        })

        return { success: true, text: nextText }
    }, [userProfile])

    const handleLoadComments = useCallback(async (moduleId, { skip = 0, limit = 10 } = {}) => {
        if (!isMongoObjectId(moduleId)) {
            return { success: false, comments: [], isNext: false }
        }

        const result = await listStoryMapComments(moduleId, {
            limit,
            skip,
        })

        if (!result.success) {
            if (skip === 0) {
                showActionNotification({
                    type: 'error',
                    message: result.message || 'Failed to load comments',
                })
            }
            return result
        }

        const commentCount = result.comments?.length ?? 0
        if (skip === 0 && commentCount > 0) {
            const meta = {
                hasComments: true,
                showCommentsIcon: true,
                commentsCount: commentCount,
            }
            setBoard((prev) => ({
                ...prev,
                journeys: prev.journeys.map((journey) => {
                    const journeyMatch = String(journey.id) === String(moduleId)
                    if (journeyMatch) {
                        return { ...journey, ...meta }
                    }
                    return {
                        ...journey,
                        steps: journey.steps.map((step) =>
                            String(step.id) === String(moduleId)
                                ? { ...step, ...meta }
                                : step
                        ),
                    }
                }),
                releases: prev.releases.map((release) => {
                    if (String(release.id) === String(moduleId)) {
                        return { ...release, ...meta }
                    }
                    return {
                        ...release,
                        stories: release.stories.map((story) =>
                            String(story.id) === String(moduleId)
                                ? { ...story, ...meta }
                                : story
                        ),
                    }
                }),
            }))
        }

        return result
    }, [])

    const handleLoadActivity = useCallback(async (moduleId, { skip = 0, limit = 20, from, to } = {}) => {
        if (!isMongoObjectId(moduleId)) {
            return { success: false, activities: [], isNext: false }
        }

        const result = await listStoryMapActivity(moduleId, {
            limit,
            skip,
            from,
            to,
        })

        if (!result.success && skip === 0) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to load activity',
            })
        }

        return result
    }, [])

    const handleLoadCardDetail = useCallback(async (cardId, cardType) => {
        if (!isMongoObjectId(cardId)) {
            return { success: false }
        }

        const module =
            cardType === 'story'
                ? 'story'
                : cardType === 'journey'
                  ? 'journey'
                  : cardType === 'release'
                    ? 'release'
                    : 'step'
        const result = await getStoryMapCard(cardId, module)

        if (!result.success || !result.detail) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to load card',
            })
            return { success: false }
        }

        if (cardType === 'journey') {
            const journey = boardRef.current.journeys?.find(
                (item) => String(item.id) === String(cardId)
            )
            updateJourneyCard(
                cardId,
                mapGetCardToBoardFields(result.detail, 'journey', {
                    existingTitle: journey?.title || '',
                })
            )
        } else if (cardType === 'release') {
            const release = boardRef.current.releases?.find(
                (item) => String(item.id) === String(cardId)
            )
            updateReleaseCard(
                cardId,
                mapGetCardToBoardFields(result.detail, 'release', {
                    existingTitle: release?.title || '',
                })
            )
        } else if (cardType === 'step') {
            let existingStep = null
            for (const journey of boardRef.current?.journeys ?? []) {
                const step = journey.steps.find(
                    (item) => String(item.id) === String(cardId)
                )
                if (step) {
                    existingStep = step
                    break
                }
            }
            const mapped = mapGetCardToBoardFields(result.detail, 'step', {
                existingTitle: existingStep?.title || '',
            })
            updateStepCard(cardId, {
                ...mapped,
                ...(!mapped.color && existingStep?.color
                    ? {
                          color: existingStep.color,
                          accentColor:
                              existingStep.accentColor ?? existingStep.color,
                      }
                    : {}),
            })
        } else {
            const storyContext = findStoryRelease(cardId)
            const mapped = mapGetCardToBoardFields(result.detail, 'story', {
                existingTitle: storyContext?.story?.title || '',
            })
            updateStoryCard(cardId, {
                ...mapped,
                ...(!mapped.color && storyContext?.story?.color
                    ? {
                          color: storyContext.story.color,
                          accentColor:
                              storyContext.story.accentColor ??
                              storyContext.story.color,
                      }
                    : {}),
            })
        }

        return { success: true }
    }, [findStoryRelease, updateJourneyCard, updateReleaseCard, updateStepCard, updateStoryCard])

    const boardStats = useMemo(
        () => ({
            journeys: board.journeys.length,
            steps: steps.length,
            releases: board.releases.length,
            stories: board.releases.reduce(
                (total, release) => total + release.stories.length,
                0
            ),
        }),
        [board.journeys.length, board.releases, steps.length]
    )

    const filterMatchCount = useMemo(
        () => countBoardFilterMatches(board),
        [board]
    )

    const commitBoardTitle = useCallback(async (name) => {
        if (readOnlyRef.current) return

        const trimmed = String(name ?? '').trim() || 'Untitled Story Map'

        setBoard((prev) => ({
            ...prev,
            title: trimmed,
        }))

        const storyMapId = boardRef.current?.id
        if (!storyMapId || !isMongoObjectId(storyMapId)) return

        const result = await updateStoryMap(String(storyMapId), { name: trimmed })
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to update story map name',
            })
        }
    }, [])

    return {
        board,
        activeDragType,
        draggingStoryReleaseId,
        storyDragOverCellKey,
        draggingStorySourceCellKey,
        draggingStepJourneyId,
        stepDragPlaceholder,
        editingJourneyId,
        editingJourneyDraft,
        editingStepId,
        editingStepDraft,
        editingStoryId,
        editingStoryDraft,
        topHScrollRef,
        topPaneRef,
        bodyPaneRef,
        journeyInputRef,
        stepInputRef,
        storyInputRef,
        boardTracks,
        gridTemplateColumns,
        boardStats,
        commitBoardTitle,
        syncHorizontalScroll,
        updateJourneyDraft,
        updateStepDraft,
        updateStoryDraft,
        commitJourneyTitle,
        commitStepTitle,
        commitStoryTitle,
        insertJourneyAt,
        insertStepAt,
        addRelease,
        addStory,
        toggleReleaseCollapse,
        handleDragStart,
        handleDragUpdate,
        handleDragEnd,
        journeyMenu,
        stepMenu,
        storyMenu,
        releaseMenu,
        selectedCardDetail,
        highlightedCardId,
        handleCopyCardLink,
        handleCopyBoardLink,
        openStepDetail,
        openStoryDetail,
        openJourneyDetail,
        openReleaseDetail,
        closeCardDetail,
        handleCardDetailUpdate,
        handleAddComment,
        handleDeleteComment,
        handleEditComment,
        handleLoadComments,
        handleLoadActivity,
        handleLoadCardDetail,
        handleBoardMouseDown,
        deleteConfirm,
        closeDeleteConfirm,
        missingDeepLinkCard,
        closeMissingDeepLinkCard,
        boardFilters,
        filterLoading,
        applyBoardFilters,
        filterMatchCount,
        filterMatchIndex,
        filterFocusedCardId,
        isFilterActive: Boolean(board.isFilterActive),
        goToNextFilterMatch,
        goToPrevFilterMatch,
        readOnly,
    }
}

export default useStoryMapBoard
